// legacy-utils.ts — Barrel re-exports + window registrations
//
// This file re-exports from focused utility modules for backward compatibility.
// Consumer code can import from '@/utils/legacy-utils' or directly from the
// focused module (e.g., '@/utils/validation').
//
// Phase 17d will remove the window registrations below once all consumers
// use ES module imports.

// ============================================================================
// Re-exports from focused modules
// ============================================================================
export { hasValue, hasStringValue, hasNumericValue } from './validation';

export {
  orderBy, arraysEqual, arraysEqualsWithIEquals,
  getCircularReplacer, deepCopyProperties, replaceObjectPropertyValuesWithValue,
  indexOfValueInArrayByProperty, getObjectInArrayByProperty, isInArrayByProperty,
  removeObjectFromArrayByProperty, isInArray, isInArrayByCompareFunc,
  removeFromArray, cleanArray, difference, sortAndRemoveDuplicates,
  isArrayEmpty, isString, generatePropertyToTrueObject,
  runPromiseFactoriesSequentially,
} from './arrays';

export {
  ModuleTypeInfo,
  getAuthObject, setAuthObject, removeAuthObject,
  getCurrentUserId, getCurrentUsername, getCurrentCompanyId, getCurrentAccessToken,
  getAvailableModules, getModuleTypeInfo, setModuleTypeAndAvailableSubmodules,
  removeModuleTypeAndAvailableSubmodules, getModuleType, getAvailableSubmodules,
  hasReportApproval, currentUserHasPermission,
  getCompanyCountry, showUsaStateInformation,
  getOfficerLabel, getOfficerPluralLabel,
} from './auth';

export {
  getCodes, getCallDisposition, getWarningPriority,
  getGender, getRace, getEyeColor, getHairColor,
  getP2PRelationship, getVehicleType,
  getNotifiedAs, getNotifiedOfIncident, getPersonRole,
  getOffense, getReportedAs, getEvidenceOf,
} from './codes';

export {
  generalLogEntryTemplate, notesAuditLogEntryTemplate,
  npsdAuditLogEntryTemplate, callCardAndReportAuditLogEntryTemplate,
  getSubmoduleOrKerktonRoleFromLogEntry, getSubmoduleOrKerktonRoleFromReportAuditLogEntry,
} from './audit-log';

export { processOfficerStatusListFromResult } from './officer-status';

export {
  locationCallbackSuccess, locationCallbackFail,
  isIpBasedGeolocationMode, setIpBasedGeolocationMode,
  getCachedGeolocationPosition, setCachedGeolocationPosition,
  getGeolocationOptions, getGeolocation,
  GEO_IP_BASED_MODE_KEY, GEO_CACHED_POSITION_KEY,
  GEO_ACCURACY_THRESHOLD_IP_BASED, GEO_ACCURACY_THRESHOLD_HIGH_ACCURACY,
  GEO_IP_BASED_CACHE_MAX_AGE,
  windowsAppSdkAppGeolocationSettingsSuccess, windowsAppSdkAppGeolocationSettingsError,
  _renderGeolocationSettingsModal,
} from './geolocation';

export {
  capitalizeFirstLetter, convertCentsToFormattedDollars,
  getFormattedDisposition, getLastChar, getInsecureUUID,
  kstTimeUTCToDisplayTimeLocalTime, displayKstTimeFromTourTimeZoneToLocalTimeZone,
} from './formatting';

export {
  getRelativeUrlForMapLocation, openPage, openPageInNewWindow, openWebpage,
  setButtonEnabled, hasImageExtension,
  isBasePageCaseReport, isBasePageCallCard, isBasePageCitation, isBasePageCallCardOrCaseReport,
  getAppOrBrowserString, scrollToElementOnMobile, scrollToLastMobileCard,
  showHomePage, refreshPage,
} from './dom';

export { formatDate, formatDate2, formatDate3 } from './date-format';

export {
  timeSinceFormatted, durationFormatted, timeSince,
  isoDatetimeToFormattedDatetime, formattedDatetimeToISODateTime, currentISODateTime,
  getCurrentTimeUTC, getNullableDateClone, nullableMomentToDate, isDateObject,
  replaceISODateTimeValuesWithFormattedDateTimeAndTimeZoneAbbrValues,
} from './date-time';

export {
  isSupportedBrowser, isAndroid, isIOSAppFromUserAgent,
  isAtLeastVersionNumber, reloadPageSkipBrowserCache, isBetaTesting,
} from './browser';

export {
  goToInProgressScreen,
  getOrnaNavigationTypeWithDefault, setLocalOrnaNavigationType,
  startTurnByTurnNavigationV2, startTurnByTurnNavigation,
  turnByTurnNavigationLoaded, isInAppNavigationAvailable,
} from './navigation-helpers';

export {
  setNPSDItemLocked, isNpsdDataEqual, npsdTypeToNpsdRelType,
  gridNPSDDataReportCaseIdTemplate, gridNPSDDataCallCardEventIdTemplate,
  gridNPSDDataReportNatureOfCallValuesTemplate, gridNPSDDataReportViewTemplate,
  saveAddNPSDData, getAttachmentInfoList,
} from './npsd-helpers';

export {
  displayNocValuesForGridTemplate, closeNotificationModals,
  getGridBackupUnitClassNameFromOfficerStatus, getCADInProgressPendingClassNameFromCallDateTime,
  processCallCardAvailableOfficers, getOfficerListStatusImageTemplateFromOfficer,
  sortNatureOfCallList, sortCallDispositionList, showCompanyNotification,
  addCompanyNameField, mergeCurrentNatureOfCallIntoNatureOfCallList,
  mergeCurrentCallDispositionIntoCallDispositionList,
  goToNewCallWithDispositionNotes,
  dialogCheckIfUserWantsToEditOrReopenCallCardWithClientNotifications,
  getCompanyVehicleListForOfficerStatusPromise, getOfficerStatusPromise,
  updateStatusWithGeolocation,
} from './call-card-helpers';

export {
  showLoadScreenIfParamTrue, hideLoadScreenIfParamTrue,
  realtimeCustomLocationUpdate, getCustomLocationListPromise,
  wrapCustomLocationOrCoordinatesInPromise, getCustomLocationOrCoordinatesFromAddressPromise,
  getTrustedLocationFromAddressPromise,
  showAddressSelectionModal, validateAddressWithSuggestions,
  validateAddressWithSuggestionsFromGeocoding,
} from './custom-location';

export {
  canCurrentUserAccessAnyClosedCalls,
  canCurrentUserAccessAnyApprovedCaseReports,
  canCurrentUserAccessCaseReport,
} from './permissions-helpers';

export { isCaseReportEqual } from './case-report-helpers';

export {
  editorAttachmentKeys, ATTACHMENT_TYPES,
  loadNocSopImages, stripImageSrcForSave,
} from './sop-helpers';

export {
  THEME_NIGHT_BLUE_COOKIE_VALUE, THEME_NIGHT_GREY_COOKIE_VALUE, THEME_DAY_COOKIE_VALUE,
  HelpDeskType, setIOSThemeLightMode, setIOSThemeDarkMode,
} from './theme';

export { getGoogleMapsPolygonBounds } from './map-helpers';

// ============================================================================
// Window Registrations (Phase 17d will remove these)
// ============================================================================
// All exported symbols must be registered on `window` so that legacy code
// (MyBuiltScripts.js) can access them via `(window as any).X`.

import { hasValue, hasStringValue, hasNumericValue } from './validation';
import {
  orderBy, arraysEqual, arraysEqualsWithIEquals,
  getCircularReplacer, deepCopyProperties, replaceObjectPropertyValuesWithValue,
  indexOfValueInArrayByProperty, getObjectInArrayByProperty, isInArrayByProperty,
  removeObjectFromArrayByProperty, isInArray, isInArrayByCompareFunc,
  removeFromArray, cleanArray, difference, sortAndRemoveDuplicates,
  isArrayEmpty, isString, generatePropertyToTrueObject,
  runPromiseFactoriesSequentially,
} from './arrays';
import {
  ModuleTypeInfo,
  getAuthObject, setAuthObject, removeAuthObject,
  getCurrentUserId, getCurrentUsername, getCurrentCompanyId, getCurrentAccessToken,
  getAvailableModules, getModuleTypeInfo, setModuleTypeAndAvailableSubmodules,
  removeModuleTypeAndAvailableSubmodules, getModuleType, getAvailableSubmodules,
  hasReportApproval, currentUserHasPermission,
  getCompanyCountry, showUsaStateInformation,
  getOfficerLabel, getOfficerPluralLabel,
} from './auth';
import {
  getCodes, getCallDisposition, getWarningPriority,
  getGender, getRace, getEyeColor, getHairColor,
  getP2PRelationship, getVehicleType,
  getNotifiedAs, getNotifiedOfIncident, getPersonRole,
  getOffense, getReportedAs, getEvidenceOf,
} from './codes';
import {
  generalLogEntryTemplate, notesAuditLogEntryTemplate,
  npsdAuditLogEntryTemplate, callCardAndReportAuditLogEntryTemplate,
  getSubmoduleOrKerktonRoleFromLogEntry, getSubmoduleOrKerktonRoleFromReportAuditLogEntry,
} from './audit-log';
import { processOfficerStatusListFromResult } from './officer-status';
import {
  locationCallbackSuccess, locationCallbackFail,
  isIpBasedGeolocationMode, setIpBasedGeolocationMode,
  getCachedGeolocationPosition, setCachedGeolocationPosition,
  getGeolocationOptions, getGeolocation,
  GEO_IP_BASED_MODE_KEY, GEO_CACHED_POSITION_KEY,
  GEO_ACCURACY_THRESHOLD_IP_BASED, GEO_ACCURACY_THRESHOLD_HIGH_ACCURACY,
  GEO_IP_BASED_CACHE_MAX_AGE,
  windowsAppSdkAppGeolocationSettingsSuccess, windowsAppSdkAppGeolocationSettingsError,
  _renderGeolocationSettingsModal,
} from './geolocation';
import {
  capitalizeFirstLetter, convertCentsToFormattedDollars,
  getFormattedDisposition, getLastChar, getInsecureUUID,
  kstTimeUTCToDisplayTimeLocalTime, displayKstTimeFromTourTimeZoneToLocalTimeZone,
} from './formatting';
import {
  getRelativeUrlForMapLocation, openPage, openPageInNewWindow, openWebpage,
  setButtonEnabled, hasImageExtension,
  isBasePageCaseReport, isBasePageCallCard, isBasePageCitation, isBasePageCallCardOrCaseReport,
  getAppOrBrowserString, scrollToElementOnMobile, scrollToLastMobileCard,
  showHomePage, refreshPage,
} from './dom';
import {
  timeSinceFormatted, durationFormatted, timeSince,
  isoDatetimeToFormattedDatetime, formattedDatetimeToISODateTime, currentISODateTime,
  getCurrentTimeUTC, getNullableDateClone, nullableMomentToDate, isDateObject,
  replaceISODateTimeValuesWithFormattedDateTimeAndTimeZoneAbbrValues,
} from './date-time';
import {
  isSupportedBrowser, isAndroid, isIOSAppFromUserAgent,
  isAtLeastVersionNumber, reloadPageSkipBrowserCache, isBetaTesting,
} from './browser';
import {
  goToInProgressScreen,
  getOrnaNavigationTypeWithDefault, setLocalOrnaNavigationType,
  startTurnByTurnNavigationV2, startTurnByTurnNavigation,
  turnByTurnNavigationLoaded, isInAppNavigationAvailable,
} from './navigation-helpers';
import {
  setNPSDItemLocked, isNpsdDataEqual, npsdTypeToNpsdRelType,
  gridNPSDDataReportCaseIdTemplate, gridNPSDDataCallCardEventIdTemplate,
  gridNPSDDataReportNatureOfCallValuesTemplate, gridNPSDDataReportViewTemplate,
  saveAddNPSDData, getAttachmentInfoList,
} from './npsd-helpers';
import {
  displayNocValuesForGridTemplate, closeNotificationModals,
  getGridBackupUnitClassNameFromOfficerStatus, getCADInProgressPendingClassNameFromCallDateTime,
  processCallCardAvailableOfficers, getOfficerListStatusImageTemplateFromOfficer,
  sortNatureOfCallList, sortCallDispositionList, showCompanyNotification,
  addCompanyNameField, mergeCurrentNatureOfCallIntoNatureOfCallList,
  mergeCurrentCallDispositionIntoCallDispositionList,
  goToNewCallWithDispositionNotes,
  dialogCheckIfUserWantsToEditOrReopenCallCardWithClientNotifications,
  getCompanyVehicleListForOfficerStatusPromise, getOfficerStatusPromise,
  updateStatusWithGeolocation,
} from './call-card-helpers';
import {
  showLoadScreenIfParamTrue, hideLoadScreenIfParamTrue,
  realtimeCustomLocationUpdate, getCustomLocationListPromise,
  wrapCustomLocationOrCoordinatesInPromise, getCustomLocationOrCoordinatesFromAddressPromise,
  getTrustedLocationFromAddressPromise,
  showAddressSelectionModal, validateAddressWithSuggestions,
  validateAddressWithSuggestionsFromGeocoding,
} from './custom-location';
import {
  canCurrentUserAccessAnyClosedCalls,
  canCurrentUserAccessAnyApprovedCaseReports,
  canCurrentUserAccessCaseReport,
} from './permissions-helpers';
import { isCaseReportEqual } from './case-report-helpers';
import {
  editorAttachmentKeys, ATTACHMENT_TYPES,
  loadNocSopImages, stripImageSrcForSave,
} from './sop-helpers';
import {
  THEME_NIGHT_BLUE_COOKIE_VALUE, THEME_NIGHT_GREY_COOKIE_VALUE, THEME_DAY_COOKIE_VALUE,
  HelpDeskType, setIOSThemeLightMode, setIOSThemeDarkMode,
} from './theme';
import { getGoogleMapsPolygonBounds } from './map-helpers';

// Functions
(window as any).orderBy = orderBy;
(window as any).timeSinceFormatted = timeSinceFormatted;
(window as any).durationFormatted = durationFormatted;
(window as any).timeSince = timeSince;
(window as any).displayNocValuesForGridTemplate = displayNocValuesForGridTemplate;
(window as any).closeNotificationModals = closeNotificationModals;
(window as any).arraysEqualsWithIEquals = arraysEqualsWithIEquals;
(window as any).arraysEqual = arraysEqual;
(window as any).processOfficerStatusListFromResult = processOfficerStatusListFromResult;
(window as any).getCodes = getCodes;
(window as any).getCallDisposition = getCallDisposition;
(window as any).getWarningPriority = getWarningPriority;
(window as any).getGender = getGender;
(window as any).getRace = getRace;
(window as any).getEyeColor = getEyeColor;
(window as any).getHairColor = getHairColor;
(window as any).getP2PRelationship = getP2PRelationship;
(window as any).getVehicleType = getVehicleType;
(window as any).getNotifiedAs = getNotifiedAs;
(window as any).getNotifiedOfIncident = getNotifiedOfIncident;
(window as any).getPersonRole = getPersonRole;
(window as any).getOffense = getOffense;
(window as any).getReportedAs = getReportedAs;
(window as any).getEvidenceOf = getEvidenceOf;
(window as any).generalLogEntryTemplate = generalLogEntryTemplate;
(window as any).notesAuditLogEntryTemplate = notesAuditLogEntryTemplate;
(window as any).npsdAuditLogEntryTemplate = npsdAuditLogEntryTemplate;
(window as any).callCardAndReportAuditLogEntryTemplate = callCardAndReportAuditLogEntryTemplate;
(window as any).isoDatetimeToFormattedDatetime = isoDatetimeToFormattedDatetime;
(window as any).formattedDatetimeToISODateTime = formattedDatetimeToISODateTime;
(window as any).currentISODateTime = currentISODateTime;
(window as any).getAuthObject = getAuthObject;
(window as any).setAuthObject = setAuthObject;
(window as any).removeAuthObject = removeAuthObject;
(window as any).getCurrentUserId = getCurrentUserId;
(window as any).getCurrentUsername = getCurrentUsername;
(window as any).getCurrentCompanyId = getCurrentCompanyId;
(window as any).getCurrentAccessToken = getCurrentAccessToken;
(window as any).getAvailableModules = getAvailableModules;
(window as any).getModuleTypeInfo = getModuleTypeInfo;
(window as any).setModuleTypeAndAvailableSubmodules = setModuleTypeAndAvailableSubmodules;
(window as any).removeModuleTypeAndAvailableSubmodules = removeModuleTypeAndAvailableSubmodules;
(window as any).hasReportApproval = hasReportApproval;
(window as any).getModuleType = getModuleType;
(window as any).getAvailableSubmodules = getAvailableSubmodules;
(window as any).goToInProgressScreen = goToInProgressScreen;
(window as any).getCircularReplacer = getCircularReplacer;
(window as any).deepCopyProperties = deepCopyProperties;
(window as any).replaceObjectPropertyValuesWithValue = replaceObjectPropertyValuesWithValue;
(window as any).replaceISODateTimeValuesWithFormattedDateTimeAndTimeZoneAbbrValues = replaceISODateTimeValuesWithFormattedDateTimeAndTimeZoneAbbrValues;
(window as any).getSubmoduleOrKerktonRoleFromLogEntry = getSubmoduleOrKerktonRoleFromLogEntry;
(window as any).getSubmoduleOrKerktonRoleFromReportAuditLogEntry = getSubmoduleOrKerktonRoleFromReportAuditLogEntry;
(window as any).setNPSDItemLocked = setNPSDItemLocked;
(window as any).getCurrentTimeUTC = getCurrentTimeUTC;
(window as any).getNullableDateClone = getNullableDateClone;
(window as any).nullableMomentToDate = nullableMomentToDate;
(window as any).isDateObject = isDateObject;
(window as any).isSupportedBrowser = isSupportedBrowser;
(window as any).getGridBackupUnitClassNameFromOfficerStatus = getGridBackupUnitClassNameFromOfficerStatus;
(window as any).getCADInProgressPendingClassNameFromCallDateTime = getCADInProgressPendingClassNameFromCallDateTime;
(window as any).showLoadScreenIfParamTrue = showLoadScreenIfParamTrue;
(window as any).hideLoadScreenIfParamTrue = hideLoadScreenIfParamTrue;
(window as any).realtimeCustomLocationUpdate = realtimeCustomLocationUpdate;
(window as any).getCustomLocationListPromise = getCustomLocationListPromise;
(window as any).wrapCustomLocationOrCoordinatesInPromise = wrapCustomLocationOrCoordinatesInPromise;
(window as any).getCustomLocationOrCoordinatesFromAddressPromise = getCustomLocationOrCoordinatesFromAddressPromise;
(window as any).getTrustedLocationFromAddressPromise = getTrustedLocationFromAddressPromise;
(window as any).showAddressSelectionModal = showAddressSelectionModal;
(window as any).validateAddressWithSuggestions = validateAddressWithSuggestions;
(window as any).validateAddressWithSuggestionsFromGeocoding = validateAddressWithSuggestionsFromGeocoding;
(window as any).gridNPSDDataReportCaseIdTemplate = gridNPSDDataReportCaseIdTemplate;
(window as any).gridNPSDDataCallCardEventIdTemplate = gridNPSDDataCallCardEventIdTemplate;
(window as any).gridNPSDDataReportNatureOfCallValuesTemplate = gridNPSDDataReportNatureOfCallValuesTemplate;
(window as any).gridNPSDDataReportViewTemplate = gridNPSDDataReportViewTemplate;
(window as any).isInArrayByCompareFunc = isInArrayByCompareFunc;
(window as any).getObjectInArrayByProperty = getObjectInArrayByProperty;
(window as any).isInArrayByProperty = isInArrayByProperty;
(window as any).indexOfValueInArrayByProperty = indexOfValueInArrayByProperty;
(window as any).removeObjectFromArrayByProperty = removeObjectFromArrayByProperty;
(window as any).isArrayEmpty = isArrayEmpty;
(window as any).saveAddNPSDData = saveAddNPSDData;
(window as any).processCallCardAvailableOfficers = processCallCardAvailableOfficers;
(window as any).getOfficerListStatusImageTemplateFromOfficer = getOfficerListStatusImageTemplateFromOfficer;
(window as any).getFormattedDisposition = getFormattedDisposition;
(window as any).capitalizeFirstLetter = capitalizeFirstLetter;
(window as any).convertCentsToFormattedDollars = convertCentsToFormattedDollars;
(window as any).isInArray = isInArray;
(window as any).setButtonEnabled = setButtonEnabled;
(window as any).difference = difference;
(window as any).getLastChar = getLastChar;
(window as any).sortAndRemoveDuplicates = sortAndRemoveDuplicates;
(window as any).removeFromArray = removeFromArray;
(window as any).cleanArray = cleanArray;
(window as any).sortNatureOfCallList = sortNatureOfCallList;
(window as any).sortCallDispositionList = sortCallDispositionList;
(window as any).showCompanyNotification = showCompanyNotification;
(window as any).openWebpage = openWebpage;
(window as any).openPage = openPage;
(window as any).openPageInNewWindow = openPageInNewWindow;
(window as any).reloadPageSkipBrowserCache = reloadPageSkipBrowserCache;
(window as any).locationCallbackSuccess = locationCallbackSuccess;
(window as any).locationCallbackFail = locationCallbackFail;
(window as any).isIpBasedGeolocationMode = isIpBasedGeolocationMode;
(window as any).setIpBasedGeolocationMode = setIpBasedGeolocationMode;
(window as any).getCachedGeolocationPosition = getCachedGeolocationPosition;
(window as any).setCachedGeolocationPosition = setCachedGeolocationPosition;
(window as any).getGeolocationOptions = getGeolocationOptions;
(window as any).getGeolocation = getGeolocation;
(window as any).windowsAppSdkAppGeolocationSettingsSuccess = windowsAppSdkAppGeolocationSettingsSuccess;
(window as any).windowsAppSdkAppGeolocationSettingsError = windowsAppSdkAppGeolocationSettingsError;
(window as any)._renderGeolocationSettingsModal = _renderGeolocationSettingsModal;
(window as any).isBasePageCaseReport = isBasePageCaseReport;
(window as any).isBasePageCallCard = isBasePageCallCard;
(window as any).isBasePageCitation = isBasePageCitation;
(window as any).isBasePageCallCardOrCaseReport = isBasePageCallCardOrCaseReport;
(window as any).getAppOrBrowserString = getAppOrBrowserString;
(window as any).hasImageExtension = hasImageExtension;
(window as any).hasValue = hasValue;
(window as any).hasStringValue = hasStringValue;
(window as any).hasNumericValue = hasNumericValue;
(window as any).isCaseReportEqual = isCaseReportEqual;
(window as any).isNpsdDataEqual = isNpsdDataEqual;
(window as any).npsdTypeToNpsdRelType = npsdTypeToNpsdRelType;
(window as any).currentUserHasPermission = currentUserHasPermission;
(window as any).refreshPage = refreshPage;
(window as any).isAndroid = isAndroid;
(window as any).getOrnaNavigationTypeWithDefault = getOrnaNavigationTypeWithDefault;
(window as any).setLocalOrnaNavigationType = setLocalOrnaNavigationType;
(window as any).startTurnByTurnNavigationV2 = startTurnByTurnNavigationV2;
(window as any).startTurnByTurnNavigation = startTurnByTurnNavigation;
(window as any).turnByTurnNavigationLoaded = turnByTurnNavigationLoaded;
(window as any).isInAppNavigationAvailable = isInAppNavigationAvailable;
(window as any).getGoogleMapsPolygonBounds = getGoogleMapsPolygonBounds;
(window as any).kstTimeUTCToDisplayTimeLocalTime = kstTimeUTCToDisplayTimeLocalTime;
(window as any).displayKstTimeFromTourTimeZoneToLocalTimeZone = displayKstTimeFromTourTimeZoneToLocalTimeZone;
(window as any).generatePropertyToTrueObject = generatePropertyToTrueObject;
(window as any).addCompanyNameField = addCompanyNameField;
(window as any).mergeCurrentNatureOfCallIntoNatureOfCallList = mergeCurrentNatureOfCallIntoNatureOfCallList;
(window as any).mergeCurrentCallDispositionIntoCallDispositionList = mergeCurrentCallDispositionIntoCallDispositionList;
(window as any).canCurrentUserAccessAnyClosedCalls = canCurrentUserAccessAnyClosedCalls;
(window as any).canCurrentUserAccessAnyApprovedCaseReports = canCurrentUserAccessAnyApprovedCaseReports;
(window as any).canCurrentUserAccessCaseReport = canCurrentUserAccessCaseReport;
(window as any).getAttachmentInfoList = getAttachmentInfoList;
(window as any).getRelativeUrlForMapLocation = getRelativeUrlForMapLocation;
(window as any).showHomePage = showHomePage;
(window as any).isAtLeastVersionNumber = isAtLeastVersionNumber;
(window as any).runPromiseFactoriesSequentially = runPromiseFactoriesSequentially;
(window as any).isString = isString;
(window as any).goToNewCallWithDispositionNotes = goToNewCallWithDispositionNotes;
(window as any).dialogCheckIfUserWantsToEditOrReopenCallCardWithClientNotifications = dialogCheckIfUserWantsToEditOrReopenCallCardWithClientNotifications;
(window as any).getCompanyCountry = getCompanyCountry;
(window as any).showUsaStateInformation = showUsaStateInformation;
(window as any).getCompanyVehicleListForOfficerStatusPromise = getCompanyVehicleListForOfficerStatusPromise;
(window as any).getOfficerStatusPromise = getOfficerStatusPromise;
(window as any).updateStatusWithGeolocation = updateStatusWithGeolocation;
(window as any).getOfficerLabel = getOfficerLabel;
(window as any).getOfficerPluralLabel = getOfficerPluralLabel;
(window as any).loadNocSopImages = loadNocSopImages;
(window as any).stripImageSrcForSave = stripImageSrcForSave;
(window as any).isBetaTesting = isBetaTesting;
(window as any).setIOSThemeLightMode = setIOSThemeLightMode;
(window as any).setIOSThemeDarkMode = setIOSThemeDarkMode;
(window as any).isIOSAppFromUserAgent = isIOSAppFromUserAgent;
(window as any).getInsecureUUID = getInsecureUUID;
(window as any).scrollToElementOnMobile = scrollToElementOnMobile;
(window as any).scrollToLastMobileCard = scrollToLastMobileCard;

// Classes and enums
(window as any).ModuleTypeInfo = ModuleTypeInfo;
(window as any).ATTACHMENT_TYPES = ATTACHMENT_TYPES;
(window as any).HelpDeskType = HelpDeskType;

// Constants
(window as any).THEME_NIGHT_BLUE_COOKIE_VALUE = THEME_NIGHT_BLUE_COOKIE_VALUE;
(window as any).THEME_NIGHT_GREY_COOKIE_VALUE = THEME_NIGHT_GREY_COOKIE_VALUE;
(window as any).THEME_DAY_COOKIE_VALUE = THEME_DAY_COOKIE_VALUE;
(window as any).GEO_IP_BASED_MODE_KEY = GEO_IP_BASED_MODE_KEY;
(window as any).GEO_CACHED_POSITION_KEY = GEO_CACHED_POSITION_KEY;
(window as any).GEO_ACCURACY_THRESHOLD_IP_BASED = GEO_ACCURACY_THRESHOLD_IP_BASED;
(window as any).GEO_ACCURACY_THRESHOLD_HIGH_ACCURACY = GEO_ACCURACY_THRESHOLD_HIGH_ACCURACY;
(window as any).GEO_IP_BASED_CACHE_MAX_AGE = GEO_IP_BASED_CACHE_MAX_AGE;

// Mutable state
(window as any).editorAttachmentKeys = editorAttachmentKeys;
